import i18next from 'i18next';
import { get } from 'lodash';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, ScrollView } from 'react-native';
import Modal from 'react-native-modal';
import { IconSvg } from 'src/assets';
import { LocationProps, SeatLayoutProps, SeatProps } from 'src/common/api/response';
import { useSeatDetailsMutation } from 'src/common/hooks';
import { useTripInfoState } from 'src/common/hooks/states/useTripInfoState';
import { useSeatSelectionListener } from 'src/common/hooks/useSeatSelectionListener';
import { Container, PrimaryButton, RegularText, Row, SizedBox, TextButton } from 'src/components';
import { Navigation } from 'src/navigations/RootNavigation';
import { SeatLayout } from 'src/screens/selectSeat/components/SeatLayout';
import { AppColors } from 'src/themes/Colors';
import { device, metrics, pixelScaler } from 'src/themes/Metrics';

interface ReselectSeatContext {
  showModal: (props: SocketBusySeatProps) => void;
  hideModal: () => void;
  isModalVisible: () => boolean;
}

const initalState: ReselectSeatContext = {
  showModal: () => {},
  hideModal: () => {},
  isModalVisible: () => false,
};

const ReselectSeatContext = React.createContext(initalState);

export const useReselectModalContext = () => React.useContext(ReselectSeatContext);
interface SocketBusySeatProps {
  busySeats: SeatProps[];
  fromLocation: LocationProps;
  toLocation: LocationProps;
  tripId: string;
  userId: string;
  isReturnTrip: boolean;
  seatsSelectedAlreadyBusy: SeatProps[];
  totalSeatSelectedCount: number;
}

// const socketData: SocketBusySeatProps = {
//   fromLocation: {},
//   toLocation: {},
//   busySeats: [],
//   tripId: '1',
//   userId: '',
//   isReturnTrip: false,
// };

export const ReselectSeatModal = ({ children }: any) => {
  const { t } = useTranslation();
  const { searchInfo, passengerInfo, setDepartureSeatsSelected, setReturnSeatsSelected } = useTripInfoState();

  const { mutate: seatDetailsMutate, data: seatDetail, isSuccess, isLoading } = useSeatDetailsMutation();
  const busySeatObj = useSeatSelectionListener({ seatDetail: seatDetail?.data?.data });

  const [modalData, setModalData] = React.useState<SocketBusySeatProps>();
  // const [tempModalData, setTempModalData] = React.useState<SocketBusySeatProps>();
  const [isVisible, setIsVisible] = React.useState<boolean>(false);

  const [vehicleSeatLayout, setVehicleSeatLayout] = React.useState<SeatLayoutProps[]>();
  const totalSeatSelectionCount = modalData?.totalSeatSelectedCount || 0;
  const tempModalDataRef = React.useRef<SocketBusySeatProps>();

  const isAllSeatsSelected = React.useMemo(() => {
    if (!vehicleSeatLayout) return false;
    const layout: any = get(vehicleSeatLayout, '[0].decks[0].layout', undefined);
    console.log({ layout });
    let result = layout.map((column: any) => {
      const filter = column.filter((item: any) => item?.status === 'available');
      return filter.length > 0 ? '' : undefined;
    });
    result = result.filter((r: any) => r !== undefined);
    console.log({ result });
    return result.length === 0;
  }, [JSON.stringify(vehicleSeatLayout)]);
  // const [prevSeatSelectedCount, setPrevSeatSelectedCount] = React.useState<number>();

  // console.log('passengerInfo', passengerInfo);

  const value = React.useMemo(
    () => ({
      isModalVisible: () => isVisible,
      hideModal: () => {
        setIsVisible(false);
        setModalData(undefined);
      },
      showModal: (props: SocketBusySeatProps) => {
        console.log('going inside', props);
        // if (!props.isReturnTrip && isVisible) {
        //   // meaning user already in changing seat and have other user booked their seat
        //   // force refresh
        //   setModalData(props);
        //   return;
        // }
        // if (!props.isReturnTrip && !isVisible) {
        //   setModalData(props);
        //   return;
        // }

        // return trip
        if (isVisible && props.isReturnTrip) {
          tempModalDataRef.current = props;
          return;
        }

        setModalData(prev => ({
          ...props,
          totalSeatSelectedCount: prev?.totalSeatSelectedCount
            ? prev?.totalSeatSelectedCount
            : props.totalSeatSelectedCount,
        }));
        setIsVisible(true);
      },
    }),
    [isVisible, modalData],
  );

  React.useEffect(() => {
    if (!isVisible) return;
    seatDetailsMutate({
      tripId: modalData?.tripId,
      date: modalData?.isReturnTrip ? searchInfo?.returnDate : searchInfo?.departureDate,
      // tripId: 41,
      // date: new Date(),
    });
  }, [isVisible, modalData]);

  React.useEffect(() => {
    if (!seatDetail?.data.data) return;
    if (seatDetail?.data.data.seatLayouts.length === 0) return;
    setVehicleSeatLayout(seatDetail?.data.data.seatLayouts);
  }, [isSuccess]);

  const renderModal = () => {
    return (
      <Modal isVisible={isVisible}>
        <Container
          style={{
            minHeight: device.height * 0.6,
            maxHeight: device.height * 0.85,
          }}
          padding={pixelScaler(26)}
          backgroundColor={AppColors.white}
          borderRadius={metrics.borderRadius}>
          {!isLoading && !isAllSeatsSelected && (
            <Row
              style={{
                paddingHorizontal: metrics.medium,
                borderWidth: 1,
                borderColor: AppColors.ratingBorder,
                borderRadius: metrics.borderRadius,
                alignSelf: 'baseline',
              }}>
              <TextButton
                fontWeight="bold"
                text="startOver"
                onPress={() => {
                  setIsVisible(false);
                  Navigation.popToTop();
                }}
              />
            </Row>
          )}
          <SizedBox height="medium" />
          <RegularText textAlign="center" fontWeight="bold">
            {i18next.t('seatsNumber', { seat: modalData?.seatsSelectedAlreadyBusy.map(seat => seat.label).join(',') })}
          </RegularText>
          <RegularText fontWeight="bold">{i18next.t('seatsHasbeenBooked')}</RegularText>

          <Container flex marginTop={metrics.medium}>
            <Row width="100%" center>
              <RegularText style={{ paddingRight: metrics.small }} color={AppColors.gray}>
                {modalData?.fromLocation?.name}
              </RegularText>
              <IconSvg.HeaderPointer fill={AppColors.gray} />
              <RegularText color={AppColors.gray}>{modalData?.toLocation?.name}</RegularText>
            </Row>
            {isLoading ? (
              <Container flex paddingTop={metrics.large}>
                <ActivityIndicator size="large" color={AppColors.primary} />
              </Container>
            ) : (
              <ScrollView
                showsVerticalScrollIndicator={false}
                style={{ flex: 1, width: '100%', paddingTop: metrics.large }}>
                <SeatLayout
                  showIndicator={false}
                  width="100%"
                  busySeatObj={busySeatObj}
                  channel={seatDetail?.data.data?.seatChannel || ''}
                  passengerSeats={
                    modalData?.isReturnTrip ? passengerInfo.passengerReturnSeats : passengerInfo.passengerDepartureSeats
                  }
                  totalSeatSelectionCount={totalSeatSelectionCount}
                  setSeatSelected={seats => {
                    if (modalData?.isReturnTrip) {
                      setReturnSeatsSelected(seats);
                    } else {
                      console.log('here');

                      // for(let i =0;i<passengerInfo.passengerDepartureSeats.length ;i+=1){
                      //   for(let j=0;j<seats.length ; j +=1){

                      //   }
                      // }
                      //  seats.map(seat => ({
                      //     ...seat,
                      //     createdAt: departedSeats.createdAt,
                      //     id: departedSeats.id,
                      //     label: departedSeats.label,
                      //     status: departedSeats.status,
                      //     type: departedSeats.type,
                      //     updatedAt: departedSeats.updatedAt,
                      //     vehicleDeckId: departedSeats.vehicleDeckId,
                      //     vehicleLayoutId: departedSeats.vehicleLayoutId,
                      //     watching: departedSeats.watching,
                      //   }));
                      // });
                      setDepartureSeatsSelected(seats);
                    }

                    // passengerDepartureSeatsRef.current = seats;
                  }}
                  // seatLayout={get(vehicleSeatLayout, `layout`, undefined) as SeatProps[][] | undefined}
                  transitId={get(vehicleSeatLayout, '[0].transitId', '')}
                  seatLayout={get(vehicleSeatLayout, '[0].decks[0].layout', undefined)}
                />
              </ScrollView>
            )}
            <Container marginTop={metrics.large}>
              <RegularText textAlign="center">
                {t('seatSelected', {
                  seatSelected: `${
                    modalData?.isReturnTrip
                      ? passengerInfo.passengerReturnSeats.length
                      : passengerInfo.passengerDepartureSeats.length
                  }/${modalData?.totalSeatSelectedCount}`,
                })}
              </RegularText>
            </Container>
            {!isLoading && (
              <Container marginTop={metrics.large}>
                {isAllSeatsSelected ? (
                  <PrimaryButton
                    text="startOver"
                    onPress={() => {
                      setIsVisible(false);
                      Navigation.popToTop();
                    }}
                  />
                ) : (
                  <PrimaryButton
                    disabled={
                      modalData?.isReturnTrip
                        ? passengerInfo.passengerReturnSeats.length !== totalSeatSelectionCount
                        : passengerInfo.passengerDepartureSeats.length !== totalSeatSelectionCount
                    }
                    text="changeSeat"
                    onPress={() => {
                      if (tempModalDataRef.current) {
                        // another socket
                        setModalData({ ...tempModalDataRef.current });
                      } else {
                        setIsVisible(false);
                      }
                    }}
                  />
                )}
              </Container>
            )}
          </Container>

          {/* <Container align="center" padding={metrics.large}>
            <SizedBox />
            <RegularText textAlign="center" color={AppColors.gray}>
              {modalData?.text}
            </RegularText>
          </Container>
          <Divider />
          <Container paddingVertical={metrics.small}>
            <TextButton
              fontWeight="bold"
              color={AppColors.primary}
              text={modalData?.buttonText || ''}
              onPress={modalData?.onPress}
            />
          </Container> */}
        </Container>
      </Modal>
    );
  };

  return (
    <ReselectSeatContext.Provider value={value}>
      {children}
      {renderModal()}
    </ReselectSeatContext.Provider>
  );
};
